// app/routes/_index.tsx
// Bare-root redirect to the default published locale.
//
// The old src/App.tsx had a LocaleCompatibilityRedirect wrapper that caught
// non-locale URLs and rewrote them to /<defaultLocale>/... — that logic now
// lives inside $locale.tsx, which by definition only matches locale-prefixed
// URLs. Without this file, '/' falls through to '$.tsx' (the splat catchall)
// and renders NotFoundPage.
//
// **Why not 'throw redirect()' in a loader?** RR v7's SPA fallback prerender
// uses every route's loader to generate '__spa-fallback.html'. A 302 from
// the '/' loader breaks that file's generation. So we redirect via meta
// http-equiv refresh (works without JS) + window.location.replace (JS-fast
// path that also preserves search+hash so e.g. /?preview=stork2026 still
// works after the redirect).

import { useEffect } from "react";

import { LOCALE_STORAGE_KEY } from "@/i18n/manifest";

const DEFAULT_LOCALE = "en";

export function meta() {
  return [
    { title: "Redirecting…" },
    { name: "robots", content: "noindex,nofollow" },
    { httpEquiv: "refresh", content: `0; url=/${DEFAULT_LOCALE}/` },
  ];
}

export default function RootIndexRedirect() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Prefer the locale the visitor previously selected (persisted by
    // LocaleProvider in localStorage as 'stork.locale'); fall back to the
    // build-time default. Preserve ?search and #hash so e.g. the preview-gate
    // bypass query survives the hop.
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    const locale = stored && /^[a-z]{2}(-[a-z]{2})?$/i.test(stored) ? stored : DEFAULT_LOCALE;
    window.location.replace(`/${locale}/${window.location.search}${window.location.hash}`);
  }, []);

  return (
    <noscript>
      <p style={{ padding: "2rem", textAlign: "center" }}>
        Redirecting to <a href={`/${DEFAULT_LOCALE}/`}>/{DEFAULT_LOCALE}/</a>…
      </p>
    </noscript>
  );
}
